import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import VuiBox from "components/VuiBox";
import { Card, Grid } from "@mui/material";
import { useApi } from "hooks/useAPI";
import { CompareService, FacebookAggregate, FacebookMetric, Region } from "services/CompareService";
import { getLocalTime } from "utils/getLocalTime";
import { FACEBOOK_METRIC_LABELS, getMetricOptions, getRegionOptions } from "./types/SelectTypes";
import { getFacebookChartOptions } from "./config/ChartConfig";
import { generateFacebookChartSeries } from "./utils/DataProcessingUtils";
import CompareChart from "./components/CompareChart";
import MetricSelector from "./components/MetricSelector";
import RegionSelector from "./components/RegionSelector";


const FacebookCompareChart = () => {
  const { get } = useApi();
  const selectedMerchant = useSelector((state: any) => state.merchants.selectedMerchant);
  const { startDate, endDate } = useSelector((state: any) => state.common);
  const [loading, setLoading] = useState(false);
  const [merchantData, setMerchantData] = useState<FacebookAggregate[]>([]);
  const [regionalData, setRegionalData] = useState<FacebookAggregate[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<FacebookMetric>("spend");
  const [selectedRegion, setSelectedRegion] = useState<Region | undefined>(undefined);

  const compareService = useMemo(() => new CompareService(get), []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedMerchant?.id || !startDate || !endDate) return;

      setLoading(true);
      try {
        const { startDateString, endDateString } = getLocalTime(startDate, endDate);
        const result = await compareService.getFacebookCompareChart({
          merchantId: selectedMerchant.id,
          startDate: startDateString,
          endDate: endDateString,
          aggregateLevel: "daily",
          region: selectedRegion
        });

        setMerchantData(result.data.merchant);
        setRegionalData(result.data.benchmark);
      } catch (error) {
        console.error("Error fetching Facebook data:", error);
        setMerchantData([]);
        setRegionalData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMerchant?.id, startDate, endDate, compareService, selectedRegion]);

  const series = useMemo(() => generateFacebookChartSeries(merchantData, regionalData, selectedMetric, selectedRegion), [merchantData, regionalData, selectedMetric, selectedRegion]);

  const options = useMemo(() => getFacebookChartOptions(selectedMetric), [selectedMetric]);

  // Handle region selection
  const handleRegionChange = (selectedOption: any) => {
    setSelectedRegion(selectedOption && selectedOption.value ? selectedOption.value as Region : undefined);
  };

  // Handle metric selection
  const handleMetricChange = (option: any) => {
    setSelectedMetric(option.value);
  };

  return (
    <Card>
      <VuiBox p={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MetricSelector
              title="Facebook Ads Performance"
              selectedMetric={selectedMetric}
              metricOptions={getMetricOptions(FACEBOOK_METRIC_LABELS)}
              onMetricChange={handleMetricChange}
            />
          </Grid>
          <Grid item xs={12}>
            <RegionSelector
              regionOptions={getRegionOptions()}
              onRegionChange={handleRegionChange}
            />
          </Grid>
        </Grid>
        <CompareChart
          title="Facebook Ads Performance"
          options={options}
          series={series}
          loading={loading}
          noDataMessage="No Facebook data available for the selected period"
        />
      </VuiBox>
    </Card>
  );
};

export default FacebookCompareChart;
